import React, { useMemo, useState, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Grid,
  Switch,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders, getCustomerId, getStoredUser } from '../auth';
import { ErrorBanner, LoadingPane, PageHeader } from '../components/page-elements';

const DETERMINED_BY_SITE = '__DETERMINED_BY_SITE__';

const CreateOrders = () => {
  const user = useMemo(() => getStoredUser(), []);
  const isCustomer = user?.role === 'customer';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId') || '';
  const isEditMode = Boolean(orderId);

  const initialFormData = {
    orderDate: new Date(),
    orderNumber: '',
    shipperName: '',
    receiverName: '',
    projectName: '',
    materialName: '',
    inventorySource: 'railcar',
    groundInventoryLot: '',
    secondaryGroundInventoryLot: '',
    railcarID: DETERMINED_BY_SITE,
    splitLoad: false,
    secondaryRailcarID: DETERMINED_BY_SITE,
    pickUpDate: new Date(),
    deliveryDate: new Date(),
    accessCode: '',
    notes: '',
  };

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [orderLoading, setOrderLoading] = useState(false);

  const [shippers, setShippers] = useState([]);
  const [receivers, setReceivers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [railcars, setRailcars] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(true);
  const [groundInventoryEnabled, setGroundInventoryEnabled] = useState(false);
  const [groundInventoryLots, setGroundInventoryLots] = useState([]);
  const [groundInventoryLoading, setGroundInventoryLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE_URL}/api/customers`, { headers: getAuthHeaders() }).then((res) => res.json()),
      fetch(`${API_BASE_URL}/api/shippers`, { headers: getAuthHeaders() }).then((res) => res.json()),
      fetch(`${API_BASE_URL}/api/receivers`, { headers: getAuthHeaders() }).then((res) => res.json()),
      fetch(`${API_BASE_URL}/api/materials`, { headers: getAuthHeaders() }).then((res) => res.json()),
      fetch(`${API_BASE_URL}/api/railcars`, { headers: getAuthHeaders() }).then((res) => res.json()),
    ])
      .then(([customersData, shippersData, receiversData, materialsData, railcarsData]) => {
        const scopedCustomer = Array.isArray(customersData) ? customersData[0] : null;
        setGroundInventoryEnabled(Boolean(scopedCustomer?.enableGroundInventory));
        setShippers(Array.isArray(shippersData) ? shippersData : []);
        setReceivers(Array.isArray(receiversData) ? receiversData : []);
        setMaterials(Array.isArray(materialsData) ? materialsData.filter((material) => material.isActive !== false) : []);
        setRailcars(Array.isArray(railcarsData) ? railcarsData : []);
        setDropdownLoading(false);
      })
      .catch(() => {
        setDropdownLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!isEditMode) return;

    setOrderLoading(true);
    fetch(`${API_BASE_URL}/api/orders/${orderId}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load order');
        return res.json();
      })
      .then((order) => {
        setFormData({
          orderDate: order.orderDate ? new Date(order.orderDate) : new Date(),
          orderNumber: order.orderNumber || '',
          shipperName: order.shipperName?._id || order.shipperName || '',
          receiverName: order.receiverName?._id || order.receiverName || '',
          projectName: order.projectName?._id || order.projectName || '',
          materialName: order.materialName?._id || order.materialName || '',
          inventorySource: order.inventorySource === 'ground' ? 'ground' : 'railcar',
          groundInventoryLot: order.groundInventoryLot?._id || order.groundInventoryLot || '',
          secondaryGroundInventoryLot: order.secondaryGroundInventoryLot?._id || order.secondaryGroundInventoryLot || '',
          railcarID: order.railcarID || DETERMINED_BY_SITE,
          splitLoad: Boolean(order.splitLoad),
          secondaryRailcarID: order.secondaryRailcarID || DETERMINED_BY_SITE,
          pickUpDate: order.pickUpDate ? new Date(order.pickUpDate) : new Date(),
          deliveryDate: order.deliveryDate ? new Date(order.deliveryDate) : new Date(),
          accessCode: order.accessCode || '',
          notes: order.notes || '',
        });
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => setOrderLoading(false));
  }, [isEditMode, orderId]);

  useEffect(() => {
    if (!formData.receiverName) {
      setProjects([]);
      setFormData((prev) => ({ ...prev, projectName: '' }));
      return;
    }

    fetch(`${API_BASE_URL}/api/projects?receiverId=${formData.receiverName}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        setProjects(data);
        setFormData((prev) => ({
          ...prev,
          projectName: data.some((p) => p._id === prev.projectName) ? prev.projectName : '',
        }));
      })
      .catch(() => {
        setProjects([]);
      });
  }, [formData.receiverName]);

  useEffect(() => {
    if (formData.inventorySource !== 'ground') return;
    setFormData((prev) => ({
      ...prev,
      railcarID: DETERMINED_BY_SITE,
      secondaryRailcarID: DETERMINED_BY_SITE,
    }));
  }, [formData.inventorySource]);

  useEffect(() => {
    if (!groundInventoryEnabled || formData.inventorySource !== 'ground') {
      setGroundInventoryLots([]);
      return;
    }

    const customerId = getCustomerId(user);
    const params = new URLSearchParams({ status: 'available' });
    if (customerId) params.set('customerId', String(customerId));
    if (formData.materialName) params.set('materialId', String(formData.materialName));

    setGroundInventoryLoading(true);
    fetch(`${API_BASE_URL}/api/ground-inventory?${params.toString()}`, {
      headers: getAuthHeaders(),
    })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(payload?.message || 'Failed to load ground inventory lots');
        }
        return payload;
      })
      .then((payload) => {
        const lots = Array.isArray(payload?.lots) ? payload.lots : [];
        const availableLots = lots.filter((lot) => Number(lot.remainingWeight || 0) > 0);
        setGroundInventoryLots(availableLots);
        setFormData((prev) => {
          if (!availableLots.length) {
            return { ...prev, groundInventoryLot: '', secondaryGroundInventoryLot: '' };
          }
          const selectedPrimaryExists = availableLots.some((lot) => String(lot._id) === String(prev.groundInventoryLot || ''));
          const selectedPrimaryId = selectedPrimaryExists ? String(prev.groundInventoryLot || '') : String(availableLots[0]._id);
          const selectedSecondaryExists = availableLots.some((lot) => String(lot._id) === String(prev.secondaryGroundInventoryLot || ''));
          const fallbackSecondaryId = String(availableLots.find((lot) => String(lot._id) !== selectedPrimaryId)?._id || '');

          return {
            ...prev,
            groundInventoryLot: selectedPrimaryId,
            secondaryGroundInventoryLot: selectedSecondaryExists ? String(prev.secondaryGroundInventoryLot || '') : fallbackSecondaryId,
          };
        });
      })
      .catch((err) => {
        setGroundInventoryLots([]);
        setError(err.message || 'Failed to load ground inventory lots');
      })
      .finally(() => setGroundInventoryLoading(false));
  }, [formData.inventorySource, formData.materialName, groundInventoryEnabled, user]);

  useEffect(() => {
    if (formData.inventorySource !== 'ground') return;
    const selectedLot = groundInventoryLots.find((lot) => String(lot._id) === String(formData.groundInventoryLot || ''));
    if (!selectedLot) return;
    const selectedLotMaterialId = String(selectedLot?.materialName?._id || selectedLot?.materialName || '');
    if (!selectedLotMaterialId) return;
    if (String(formData.materialName || '') === selectedLotMaterialId) return;
    setFormData((prev) => ({ ...prev, materialName: selectedLotMaterialId }));
  }, [formData.inventorySource, formData.groundInventoryLot, formData.materialName, groundInventoryLots]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'materialName' && prev.inventorySource === 'ground') {
        const matchedLot = groundInventoryLots.find((lot) => {
          const lotMaterialId = String(lot?.materialName?._id || lot?.materialName || '');
          return lotMaterialId === String(value || '');
        });
        next.groundInventoryLot = matchedLot ? String(matchedLot._id) : '';
        const matchedSecondaryLot = groundInventoryLots.find(
          (lot) => String(lot._id) !== String(next.groundInventoryLot || '')
        );
        next.secondaryGroundInventoryLot = matchedSecondaryLot ? String(matchedSecondaryLot._id) : '';
      }
      if (name === 'inventorySource' && value !== 'ground') {
        next.groundInventoryLot = '';
        next.secondaryGroundInventoryLot = '';
      }
      if (name === 'groundInventoryLot' && prev.inventorySource === 'ground') {
        if (String(value || '') === String(prev.secondaryGroundInventoryLot || '')) {
          const fallbackSecondaryLot = groundInventoryLots.find((lot) => String(lot._id) !== String(value || ''));
          next.secondaryGroundInventoryLot = fallbackSecondaryLot ? String(fallbackSecondaryLot._id) : '';
        }
      }
      return next;
    });
  };

  const handleDateChange = (name, date) => {
    setFormData((prev) => ({ ...prev, [name]: date }));
  };

  const handleSplitLoadToggle = (event) => {
    const enabled = Boolean(event.target.checked);
    setFormData((prev) => ({
      ...prev,
      splitLoad: enabled,
      secondaryRailcarID: enabled ? prev.secondaryRailcarID : DETERMINED_BY_SITE,
      secondaryGroundInventoryLot: enabled ? prev.secondaryGroundInventoryLot : '',
    }));
  };

  const railcarOptions = useMemo(() => {
    const rows = Array.isArray(railcars) ? railcars : [];
    return rows
      .map((railcar) => ({
        id: railcar._id,
        railcarID: railcar.railcarID,
        status: railcar.currentStatus || 'Unknown',
      }))
      .filter((railcar) => Boolean(railcar.railcarID))
      .sort((a, b) =>
        String(a.railcarID).localeCompare(String(b.railcarID), undefined, { numeric: true, sensitivity: 'base' })
      );
  }, [railcars]);

  if (!isCustomer) {
    return <Typography>Only customer users can create new truck orders.</Typography>;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!user?._id) {
      setError('User not logged in.');
      setLoading(false);
      return;
    }

    const customerId = getCustomerId(user);
    if (!customerId) {
      setError('User does not have a customer assigned.');
      setLoading(false);
      return;
    }

    const orderData = {
      ...formData,
      inventorySource: formData.inventorySource === 'ground' ? 'ground' : 'railcar',
      groundInventoryLot: formData.inventorySource === 'ground' ? (formData.groundInventoryLot || '') : null,
      secondaryGroundInventoryLot:
        formData.inventorySource === 'ground' && formData.splitLoad ? (formData.secondaryGroundInventoryLot || '') : null,
      railcarID: formData.railcarID === DETERMINED_BY_SITE ? '' : formData.railcarID,
      splitLoad: Boolean(formData.splitLoad),
      secondaryRailcarID:
        formData.inventorySource === 'ground'
          ? ''
          : formData.splitLoad && formData.secondaryRailcarID !== DETERMINED_BY_SITE
          ? formData.secondaryRailcarID
          : '',
      orderDate: formData.orderDate.toISOString(),
      pickUpDate: formData.pickUpDate.toISOString(),
      deliveryDate: formData.deliveryDate.toISOString(),
      customerName: customerId,
      createdBy: user._id,
      orderStatus: 'Draft',
    };

    if (orderData.inventorySource !== 'ground' && orderData.splitLoad && !orderData.secondaryRailcarID) {
      setError('Please select a secondary railcar when Split Load is enabled.');
      setLoading(false);
      return;
    }

    if (orderData.inventorySource === 'ground' && !orderData.groundInventoryLot) {
      setError('Please select a ground inventory lot.');
      setLoading(false);
      return;
    }

    if (orderData.inventorySource === 'ground' && orderData.splitLoad && !orderData.secondaryGroundInventoryLot) {
      setError('Please select a secondary ground inventory lot when Split Load is enabled.');
      setLoading(false);
      return;
    }

    if (
      orderData.inventorySource === 'ground'
      && orderData.splitLoad
      && String(orderData.groundInventoryLot) === String(orderData.secondaryGroundInventoryLot)
    ) {
      setError('Primary and secondary ground inventory lots must be different.');
      setLoading(false);
      return;
    }

    if (orderData.railcarID && orderData.secondaryRailcarID && orderData.railcarID === orderData.secondaryRailcarID) {
      setError('Primary and secondary railcars must be different.');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(isEditMode ? `${API_BASE_URL}/api/orders/${orderId}` : `${API_BASE_URL}/api/orders`, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: getAuthHeaders(true),
        body: JSON.stringify(orderData),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || `Failed to ${isEditMode ? 'update' : 'create'} order`);
      }
      setSuccess(`Order ${isEditMode ? 'updated' : 'created'} successfully as Draft.`);
      setFormData(initialFormData);
      setProjects([]);
      setTimeout(() => navigate('/orders'), 600);
    } catch (err) {
      setError(err.message);
    }

    setLoading(false);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={2}>
        <PageHeader
          title={isEditMode ? 'Edit Draft Order' : 'Create New Order'}
          subtitle="Build and save customer orders in draft before submitting."
          actions={
            <Button
              type="submit"
              form="order-draft-form"
              variant="contained"
              color="primary"
              disabled={loading || orderLoading || dropdownLoading}
            >
              {loading ? 'Saving...' : isEditMode ? 'Save Draft Changes' : 'Save Draft'}
            </Button>
          }
        />
        <ErrorBanner message={error} />
        {success && <Typography color="primary" sx={{ mb: 1.5 }}>{success}</Typography>}
        {orderLoading ? (
          <LoadingPane label="Loading order draft..." />
        ) : (
        <Box component="form" id="order-draft-form" onSubmit={handleSubmit} noValidate autoComplete="off">
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
            <CardContent>
              <Grid container spacing={1.2}>
                <Grid item xs={12} md={6}>
          <DateTimePicker
            label="Order Date"
            value={formData.orderDate}
            onChange={(date) => handleDateChange('orderDate', date)}
            renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
          />
                </Grid>
                <Grid item xs={12} md={6}>
          <TextField
            label="Order Number"
            name="orderNumber"
            value={formData.orderNumber}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {dropdownLoading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Card variant="outlined" sx={{ borderRadius: 2, mb: 1.5 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.8 }}>Routing</Typography>
                  <Grid container spacing={1.2}>
                    <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="shipperName-label">Shipper</InputLabel>
                <Select
                  labelId="shipperName-label"
                  id="shipperName"
                  name="shipperName"
                  value={formData.shipperName}
                  onChange={handleChange}
                  label="Shipper"
                  required
                >
                  {shippers.map((shipper) => (
                    <MenuItem key={shipper._id} value={shipper._id}>
                      {shipper.shipperName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>

              <FormControl fullWidth margin="normal">
                <InputLabel id="receiverName-label">Receiver</InputLabel>
                <Select
                  labelId="receiverName-label"
                  id="receiverName"
                  name="receiverName"
                  value={formData.receiverName}
                  onChange={handleChange}
                  label="Receiver"
                  required
                >
                  {receivers.map((receiver) => (
                    <MenuItem key={receiver._id} value={receiver._id}>
                      {receiver.receiverName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>

              <FormControl fullWidth margin="normal" disabled={!formData.receiverName}>
                <InputLabel id="projectName-label">Location</InputLabel>
                <Select
                  labelId="projectName-label"
                  id="projectName"
                  name="projectName"
                  value={formData.projectName}
                  onChange={handleChange}
                  label="Location"
                  required
                >
                  {projects.map((project) => (
                    <MenuItem key={project._id} value={project._id}>
                      {project.projectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>

              <FormControl fullWidth margin="normal">
                <InputLabel id="materialName-label">Material</InputLabel>
                <Select
                  labelId="materialName-label"
                  id="materialName"
                  name="materialName"
                  value={formData.materialName}
                  onChange={handleChange}
                  label="Material"
                  required
                >
                  {materials.map((material) => (
                    <MenuItem key={material._id} value={material._id}>
                      {material.refNum ? `${material.materialName} (${material.refNum})` : material.materialName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      {groundInventoryEnabled ? (
                        <FormControl fullWidth margin="normal">
                          <InputLabel id="inventory-source-label">Inventory Source</InputLabel>
                          <Select
                            labelId="inventory-source-label"
                            name="inventorySource"
                            value={formData.inventorySource}
                            onChange={handleChange}
                            label="Inventory Source"
                          >
                            <MenuItem value="railcar">Railcar</MenuItem>
                            <MenuItem value="ground">Ground Inventory</MenuItem>
                          </Select>
                        </FormControl>
                      ) : null}
                    </Grid>
                    {groundInventoryEnabled && formData.inventorySource === 'ground' ? (
                      <Grid item xs={12}>
                        <FormControl fullWidth margin="normal">
                          <InputLabel id="ground-lot-label">Ground Inventory Lot</InputLabel>
                          <Select
                            labelId="ground-lot-label"
                            name="groundInventoryLot"
                            value={formData.groundInventoryLot || ''}
                            onChange={handleChange}
                            label="Ground Inventory Lot"
                          >
                            {groundInventoryLots.length === 0 ? (
                              <MenuItem disabled value="">
                                {groundInventoryLoading ? 'Loading lots...' : 'No available ground lots'}
                              </MenuItem>
                            ) : null}
                            {groundInventoryLots.map((lot) => (
                              <MenuItem key={lot._id} value={lot._id}>
                                {`${lot.materialName?.materialName || 'Material'} | ${lot.sourceRailcarID || 'Manual Lot'} - ${Number(lot.remainingWeight || 0).toLocaleString()} lbs`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    ) : (
                      <Grid item xs={12}>

              <Autocomplete
                fullWidth
                options={[DETERMINED_BY_SITE, ...railcarOptions.map((railcar) => railcar.railcarID)]}
                value={formData.railcarID || DETERMINED_BY_SITE}
                onChange={(_, value) =>
                  setFormData((prev) => ({
                    ...prev,
                    railcarID: value || DETERMINED_BY_SITE,
                  }))
                }
                getOptionLabel={(option) => {
                  if (option === DETERMINED_BY_SITE) return 'Determined by Site';
                  const match = railcarOptions.find((railcar) => railcar.railcarID === option);
                  return match ? `${match.railcarID} (${match.status})` : String(option || '');
                }}
                isOptionEqualToValue={(option, value) => option === value}
                renderInput={(params) => (
                  <TextField {...params} margin="normal" label="Preferred Railcar" placeholder="Search railcar ID" />
                )}
              />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Switch checked={Boolean(formData.splitLoad)} onChange={handleSplitLoadToggle} />}
                        label={formData.inventorySource === 'ground'
                          ? 'Split Load (multiple pull sequence)'
                          : 'Split Load (designate a second railcar)'}
                      />
                    </Grid>
                    {formData.inventorySource !== 'ground' && formData.splitLoad ? (
                      <Grid item xs={12}>
                        <Autocomplete
                          fullWidth
                          options={[
                            DETERMINED_BY_SITE,
                            ...railcarOptions
                              .filter((railcar) => railcar.railcarID !== formData.railcarID)
                              .map((railcar) => railcar.railcarID),
                          ]}
                          value={formData.secondaryRailcarID || DETERMINED_BY_SITE}
                          onChange={(_, value) =>
                            setFormData((prev) => ({
                              ...prev,
                              secondaryRailcarID: value || DETERMINED_BY_SITE,
                            }))
                          }
                          getOptionLabel={(option) => {
                            if (option === DETERMINED_BY_SITE) return 'Determined by Site';
                            const match = railcarOptions.find((railcar) => railcar.railcarID === option);
                            return match ? `${match.railcarID} (${match.status})` : String(option || '');
                          }}
                          isOptionEqualToValue={(option, value) => option === value}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              margin="normal"
                              label="Secondary Preferred Railcar"
                              placeholder="Search railcar ID"
                            />
                          )}
                        />
                      </Grid>
                    ) : null}
                    {formData.inventorySource === 'ground' && formData.splitLoad ? (
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            mt: 0.5,
                            p: 1.25,
                            borderRadius: 1.5,
                            border: '1px solid var(--ui-border)',
                            background: 'linear-gradient(135deg, #f5f2ec 0%, #ffffff 100%)',
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 0.4 }}>
                            Ground Split Load Enabled
                          </Typography>
                          <FormControl fullWidth margin="normal">
                            <InputLabel id="secondary-ground-lot-label">Secondary Ground Lot</InputLabel>
                            <Select
                              labelId="secondary-ground-lot-label"
                              name="secondaryGroundInventoryLot"
                              value={formData.secondaryGroundInventoryLot || ''}
                              onChange={handleChange}
                              label="Secondary Ground Lot"
                            >
                              {groundInventoryLots.filter((lot) => String(lot._id) !== String(formData.groundInventoryLot || '')).length === 0 ? (
                                <MenuItem disabled value="">
                                  No secondary lots available
                                </MenuItem>
                              ) : null}
                              {groundInventoryLots
                                .filter((lot) => String(lot._id) !== String(formData.groundInventoryLot || ''))
                                .map((lot) => (
                                  <MenuItem key={lot._id} value={lot._id}>
                                    {`${lot.materialName?.materialName || 'Material'} | ${lot.sourceRailcarID || 'Manual Lot'} - ${Number(lot.remainingWeight || 0).toLocaleString()} lbs`}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                          <Typography variant="body2" color="text.secondary">
                            This order will use two ground lots. Secondary pull weights are entered during BOL completion.
                          </Typography>
                        </Box>
                      </Grid>
                    ) : null}
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.8 }}>Schedule & Notes</Typography>
              <Grid container spacing={1.2}>
                <Grid item xs={12} md={6}>
          <DateTimePicker
            label="Pick Up Date"
            value={formData.pickUpDate}
            onChange={(date) => handleDateChange('pickUpDate', date)}
            renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
          />
                </Grid>

                <Grid item xs={12} md={6}>
          <DateTimePicker
            label="Delivery Date"
            value={formData.deliveryDate}
            onChange={(date) => handleDateChange('deliveryDate', date)}
            renderInput={(params) => <TextField {...params} fullWidth margin="normal" />}
          />
                </Grid>

                <Grid item xs={12} md={6}>
          <TextField label="Access Code" name="accessCode" value={formData.accessCode} onChange={handleChange} fullWidth margin="normal" />
                </Grid>
                <Grid item xs={12}>
          <TextField label="Notes" name="notes" value={formData.notes} onChange={handleChange} fullWidth margin="normal" multiline rows={3} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Box mt={2}>
            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ minHeight: 44, borderRadius: 1.5 }}>
              {loading ? 'Saving...' : isEditMode ? 'Save Draft Changes' : 'Save Draft'}
            </Button>
          </Box>
        </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default CreateOrders;
